/**
 * Verifiable parental consent, from the parent's side (2026-10-16).
 *
 * The server holds the receipt and enforces it (services/api/valley/
 * consent.py); this file is the four calls and the shape they return. The
 * method is "email plus": the parent reads the notice here and affirms they
 * are the child's parent, the server emails a single-use link to the address
 * their account verified, and following it -- signed in as the same parent --
 * records consent. A confirmation email follows a day later.
 */

import { api } from '../api/client';
import { NOTICE_VERSION } from '../privacy/notice';

export type ConsentState = 'active' | 'pending' | 'revoked' | 'renewal-required' | 'none';

export interface ConsentStatus {
  required: boolean;
  state: ConsentState;
  noticeVersion: string;
  consentNotice?: string | null;
  consentAt?: string | null;
  method?: string | null;
  confirmSentAt?: string | null;
  revokedAt?: string | null;
  pendingUntil?: string | null;
  paused: boolean;
  /** Only on the reply to `askConsent`: where the link went, masked. */
  sentTo?: string | null;
}

const base = (familyId: string, personId: string) =>
  `/v1/families/${familyId}/people/${personId}`;

export function consentStatus(familyId: string, personId: string): Promise<ConsentStatus> {
  return api.get<ConsentStatus>(`${base(familyId, personId)}/privacy`);
}

export function askConsent(familyId: string, personId: string): Promise<ConsentStatus> {
  return api.write<ConsentStatus>('POST', `${base(familyId, personId)}/consent`, {
    affirm: true,
    noticeVersion: NOTICE_VERSION,
  });
}

export function confirmConsent(familyId: string, personId: string, token: string): Promise<ConsentStatus> {
  return api.write<ConsentStatus>('POST', `${base(familyId, personId)}/consent/confirm`, { token });
}

export function withdrawConsent(familyId: string, personId: string): Promise<ConsentStatus> {
  return api.write<ConsentStatus>('DELETE', `${base(familyId, personId)}/consent`);
}

export function setPaused(familyId: string, personId: string, paused: boolean): Promise<{ paused: boolean }> {
  return api.write<{ paused: boolean }>('PUT', `${base(familyId, personId)}/privacy`, { paused });
}

/** Everything the server holds about one child: see consent.data_report. */
export interface ChildDataReport {
  person: {
    id: string;
    status: string;
    created?: string | null;
    character?: Sealed | null;
    characterUpdated?: string | null;
    draftWaiting?: Sealed | null;
    draftSent?: string | null;
    previousCharacter?: Sealed | null;
    childCode?: string | null;
  };
  devices: Array<{
    id: string;
    kind?: string | null;
    status: string;
    revoked: boolean;
    created?: string | null;
    approvedAt?: string | null;
    lastSeenDate?: string | null;
    publicKey?: string | null;
    label?: Sealed | null;
  }>;
  moneyRows: { count: number; note: string };
  goals: Array<{ id: string; status?: string; inGoal: number; title?: Sealed | null }>;
  consent: ConsentStatus;
  notHeld: string[];
}

export interface Sealed {
  encrypted: true;
  bytes: number;
}

export function childData(familyId: string, personId: string): Promise<ChildDataReport> {
  return api.get<ChildDataReport>(`${base(familyId, personId)}/data`);
}

/** Does this child need a parent's permission before they can use Birbly? */
export function needsPermission(required: boolean, status: ConsentStatus | undefined): boolean {
  if (!required) return false;
  return !status || (status.state !== 'active');
}

// -- the link in the email -----------------------------------------------------

const PENDING_KEY = 'birbly.consent-link';

export interface ConsentLink {
  familyId: string;
  personId: string;
  token: string;
}

/**
 * Read `/consent#f=..&p=..&t=..`. The token is in the fragment so it never
 * reaches a server in a URL, and it is removed from the address bar at once.
 * It is held in sessionStorage across a sign-in redirect, and nowhere else.
 */
export function takeConsentLink(location: Location = window.location): ConsentLink | null {
  if (location.pathname.replace(/\/+$/, '') === '/consent' && location.hash.length > 1) {
    const params = new URLSearchParams(location.hash.slice(1));
    const link = { familyId: params.get('f') ?? '', personId: params.get('p') ?? '', token: params.get('t') ?? '' };
    window.history.replaceState({}, '', '/');
    if (/^[\w-]{1,64}$/.test(link.familyId) && /^[\w-]{1,64}$/.test(link.personId) && /^[\w-]{20,100}$/.test(link.token)) {
      try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(link));
      } catch {
        /* private mode: it still works this once */
      }
      return link;
    }
    return null;
  }
  try {
    const held = sessionStorage.getItem(PENDING_KEY);
    return held ? (JSON.parse(held) as ConsentLink) : null;
  } catch {
    return null;
  }
}

export function forgetConsentLink(): void {
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* nothing held */
  }
}
